// Mapeo FHIR del Test de Denver II (028) — mismo patrón que el odontograma
// (033, ver formulario/discretos/odontograma/fhir.ts): un único `Observation`
// con un `component` por ítem evaluado (nunca un recurso vacío), + una
// `Condition` solo si la clasificación presuntiva es Dudoso/Anormal (mismo
// criterio de "solo crear recursos con consumidor real" — Normal no necesita
// aparecer en el problem list del paciente). Funciones puras, testeadas sin
// Medplum real.
import type { Encounter, Observation, ObservationComponent, Reference } from '@medplum/fhirtypes';
import { CATALOGO_ITEMS_DENVER, CLASIFICACIONES_DENVER, type ClasificacionDenver, type ResultadoItemDenver } from '@logiacare/hce-pdf';

export const SISTEMA_ITEM_DENVER = 'http://logiacare.ec/fhir/denver-ii-item';
export const SISTEMA_RESULTADO_DENVER = 'http://logiacare.ec/fhir/denver-ii-resultado';
export const SISTEMA_CLASIFICACION_DENVER = 'http://logiacare.ec/fhir/denver-ii-clasificacion';

const ETIQUETA_RESULTADO: Record<ResultadoItemDenver, string> = {
  aprobado: 'Aprobado',
  fallado: 'Fallado',
  rehuso: 'Rehusó',
  'sin-oportunidad': 'Sin oportunidad',
};

function componenteDeItem(itemId: string, resultado: ResultadoItemDenver): ObservationComponent | undefined {
  const item = CATALOGO_ITEMS_DENVER.find((i) => i.id === itemId);
  if (!item) return undefined;
  return {
    code: { coding: [{ system: SISTEMA_ITEM_DENVER, code: itemId, display: item.texto }] },
    valueCodeableConcept: { coding: [{ system: SISTEMA_RESULTADO_DENVER, code: resultado, display: ETIQUETA_RESULTADO[resultado] }] },
  };
}

/** Arma el `Observation` único (un `component` por ítem evaluado + edad + clasificación). `undefined` si no hay ningún ítem evaluado — no crear un recurso vacío. */
export function construirObservationDenver(
  estado: Readonly<Record<string, ResultadoItemDenver>>,
  edadEvaluacionMeses: number | undefined,
  clasificacion: ClasificacionDenver | undefined,
  subject: Observation['subject'],
  encounter: Reference<Encounter> | undefined,
  edadGestacionalSemanas?: number,
): Observation | undefined {
  const entradas = Object.entries(estado);
  if (entradas.length === 0) return undefined;

  const component: ObservationComponent[] = entradas.map(([itemId, resultado]) => componenteDeItem(itemId, resultado)).filter((c): c is ObservationComponent => c !== undefined);
  if (edadEvaluacionMeses !== undefined) {
    component.push({ code: { coding: [{ system: SISTEMA_ITEM_DENVER, code: 'edad-evaluacion-meses' }] }, valueInteger: edadEvaluacionMeses });
  }

  if (edadGestacionalSemanas !== undefined) {
    component.push({ code: { coding: [{ system: SISTEMA_ITEM_DENVER, code: 'edad-gestacional-semanas' }] }, valueInteger: edadGestacionalSemanas });
  }

  return {
    resourceType: 'Observation',
    status: 'final',
    code: { text: 'Test de Denver II' },
    subject,
    encounter,
    effectiveDateTime: new Date().toISOString(),
    component,
    ...(clasificacion
      ? { valueCodeableConcept: { coding: [{ system: SISTEMA_CLASIFICACION_DENVER, code: clasificacion, display: CLASIFICACIONES_DENVER.find((c) => c.clave === clasificacion)?.etiqueta }] } }
      : {}),
  };
}

/** `true` si la clasificación amerita una `Condition` visible en el problem list — Normal no, Dudoso/Anormal sí (necesitan seguimiento). */
export function clasificacionRequiereCondition(clasificacion: ClasificacionDenver | undefined): boolean {
  return clasificacion === 'dudoso' || clasificacion === 'anormal';
}
